import { Box, BoxProps, Button, Flex, Icon, Link, Text } from '@chakra-ui/react';
import { useResource } from 'datx-jsonapi-react';
import NextLink from 'next/link';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import InfoIcon from '@/assets/icons/ic-info.svg';
import ContactUs from '@/assets/icons/ic-nav-help-desk.svg';
import TicketIcon from '@/assets/icons/ic-ticket.svg';

import { useQuery } from '@tanstack/react-query';

import { EventTicketDescriptionMarkdown } from '@/components/ui/shared/markdowns/EventTicketDescriptionMarkdown/EventTicketDescriptionMarkdown';
import { queryThinEvent } from '@/queries/thin-events';
import { sessionQueryOptions } from '@/queries/upgrade/sessions';

interface IExternalAAAProps extends BoxProps {
  eventId: string;
}

export const ExternalAAA: FC<IExternalAAAProps> = ({ eventId, ...props }) => {
  const { t } = useTranslation();
  const { data: session } = useQuery(sessionQueryOptions());
  const user = session?.user;

  const { data: event } = useResource(queryThinEvent(eventId));

  return (
    <Box shadow="card" bgColor="white" borderRadius={2} p={4} {...props}>
      {user && <NoTicketGrayBox />}
      {!user && (
        <Box borderRadius={2} bgColor="gray.50" p={4} mb={{ base: 4, lg: 8 }}>
          <Flex justifyContent="flex-start" alignItems="center" mb={2}>
            <Icon w={6} h={6} as={TicketIcon} mr={2} color="black" />
            <Text as="h4" fontSize="1.125rem" lineHeight="short">
              {t('eventTicketRegister.registrationFeeTitle')}
            </Text>
          </Flex>
          {event.missingTicketDescription ? (
            <EventTicketDescriptionMarkdown source={event.missingTicketDescription} />
          ) : (
            <Text>{t('eventTicketRegister.registrationFeeMessage')}</Text>
          )}
        </Box>
      )}
      <Flex>
        <Flex
          justifyContent="space-between"
          flexDirection={{ base: 'column', md: 'row' }}
          gap={2}
          alignItems={{ base: 'flex-start', md: 'center' }}
          w="full"
        >
          {!user && (
            <Flex justifyContent="flex-start" alignItems="center">
              <Text as="span" mr={4}>
                {t('eventTicketRegister.alreadyRegistered')}
              </Text>
              <NextLink href={event?.externalLoginUrl} passHref>
                <Button size="sm">{t('eventTicketRegister.logIn')}</Button>
              </NextLink>
            </Flex>
          )}
          <Box>
            <Text as="span" mr={4}>
              {t('eventTicketRegister.needHelp')}
            </Text>
            <NextLink
              href={{
                pathname: '/contact-us/support',
                query: { eventId: event.id },
              }}
              passHref
              legacyBehavior
            >
              <Link fontWeight="bold">
                {t('eventTicketRegister.contactUs')} <Icon as={ContactUs} ml={2} />
              </Link>
            </NextLink>
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
};

function NoTicketGrayBox() {
  const { t } = useTranslation();

  return (
    <Box borderRadius="2" bgColor="gray.50" p={4} mb={6}>
      <Flex justifyContent="flex-start" alignItems="center" mb={2}>
        <Icon w={6} h="1.5rem" as={InfoIcon} mr={2} color="red.500" />
        <Text as="h4" fontSize="1.125rem" lineHeight="short">
          {t('eventTicketRegister.externalAAANoTicketTitle')}
        </Text>
      </Flex>
      <Text>
        It looks like you might not have a ticket for this webinar. <br />
        <Link
          ml="1"
          fontWeight="semibold"
          color="blue.500"
          href="https://americananthro.org/events/virtual-events/anthropology-live/"
        >
          Please register here
        </Link>
      </Text>
    </Box>
  );
}
